import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import MDBox from "shared/components/MDBox";
import MDButton from "shared/components/MDButton";
import useFormZone from "../../../hooks/useFormZone";
import { getAllCommerce } from "modules/bitacora/services/Configuracion/Comercio/comercioService";

const FormZone = ({
  onSubmit,
  afterSubmit = () => {},
  initialData = {},
  isEditMode = false,
  loading: externalLoading = false,
}) => {
  const formik = useFormZone(onSubmit, afterSubmit, initialData, isEditMode);
  const [commerces, setCommerces] = useState([]);
  const [loadingCommerces, setLoadingCommerces] = useState(true);
  const [errorCommerces, setErrorCommerces] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadCommerces = async () => {
      try {
        const commercesData = await getAllCommerce();
        if (isMounted) {
          setCommerces(Array.isArray(commercesData) ? commercesData : []);
        }
      } catch (error) {
        if (isMounted) {
          setErrorCommerces("Error al cargar comercios");
          console.error("Error loading comercios:", error);
        }
      } finally {
        if (isMounted) {
          setLoadingCommerces(false);
        }
      }
    };

    loadCommerces();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Card>
      <MDBox p={3}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Comercio */}
            <Grid item xs={6}>
              <FormControl
                fullWidth
                error={formik.touched.commerceId && Boolean(formik.errors.commerceId)}
              >
                <InputLabel id="commerce-label">Comercio</InputLabel>
                <Select
                  labelId="commerce-label"
                  label="Comercio"
                  name="commerceId"
                  value={formik.values.commerceId || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={loadingCommerces || formik.isSubmitting || externalLoading}
                  sx={{ height: 45 }}
                >
                  {loadingCommerces ? (
                    <MenuItem disabled>Cargando comercios...</MenuItem>
                  ) : errorCommerces ? (
                    <MenuItem disabled>{errorCommerces}</MenuItem>
                  ) : (
                    commerces.map((commerce) => (
                      <MenuItem key={commerce.id} value={commerce.id}>
                        {commerce.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                <FormHelperText>
                  {formik.touched.commerceId && formik.errors.commerceId}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* Nombre */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                name="title"
                label="Nombre de la Zona"
                value={formik.values.title || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
                disabled={formik.isSubmitting || externalLoading}
              />
            </Grid>

            {/* Descripción */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                name="description"
                label="Descripción de la Zona"
                value={formik.values.description || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
                disabled={formik.isSubmitting || externalLoading}
              />
            </Grid>

            {/* Status boolean */}
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="status-label">Estado</InputLabel>
                <Select
                  labelId="status-label"
                  label="Estado"
                  name="status"
                  value={formik.values.status}
                  onChange={(e) => formik.setFieldValue("status", e.target.value)}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting || externalLoading}
                  sx={{ height: 45 }}
                >
                  <MenuItem value={true}>Activo</MenuItem>
                  <MenuItem value={false}>Inactivo</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Botón */}
            <Grid item xs={12}>
              <MDButton
                type="submit"
                variant="gradient"
                color="info"
                fullWidth
                disabled={
                  !formik.isValid || formik.isSubmitting || loadingCommerces || externalLoading
                }
              >
                {formik.isSubmitting || externalLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : isEditMode ? (
                  "Actualizar zona"
                ) : (
                  "Crear zona"
                )}
              </MDButton>
            </Grid>
          </Grid>
        </form>
      </MDBox>
    </Card>
  );
};

FormZone.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  afterSubmit: PropTypes.func,
  initialData: PropTypes.shape({
    commerceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.bool,
  }),
  isEditMode: PropTypes.bool,
  loading: PropTypes.bool,
};

FormZone.defaultProps = {
  afterSubmit: () => {},
  initialData: { status: true },
  isEditMode: false,
  loading: false,
};

export default FormZone;
